import { usePublish } from '@atlaskit/rovo-triggers';
import { type EnvironmentType, ExtensionEnvironment } from '@atlassian/forge-ui-types';
import type ApolloClient from 'apollo-client';
import { getRovoProductFromExtensionType } from '../utils/getRovoProductFromExtensionType';
import {
	getRovoAgentId,
	DEFAULT_AGENT_REFERENCE,
} from '../../../web-client/queries/getRovoAgentId';
import type { OpenRovoPayload } from '..';

const ROVO_TOPIC = 'ai-mate';
const FORGE_SOURCE = 'forge-bridge-open-rovo';
const ROVO_EVENT_TYPE = 'chat-new';

const isRestrictedAppEnvironment = (environmentType: EnvironmentType, appId: string) => {
	const ALLOWED_INTERNAL_APP_IDS = ['41dcbde8-321e-4e36-8b17-6f7697794ea6'];
	return (
		environmentType === ExtensionEnvironment.PRODUCTION && !ALLOWED_INTERNAL_APP_IDS.includes(appId)
	);
};

export function useRovoAgentsChat(
	extensionType: string,
	environmentType: EnvironmentType,
	appId: string,
	cloudId: string | undefined,
	apolloClient: ApolloClient<object>,
) {
	const publish = usePublish(ROVO_TOPIC);

	return {
		openRovo: async ({
			agentKey,
			agentName,
			prompt,
		}: OpenRovoPayload): Promise<false | undefined> => {
			// TODO: Remove this check once the feature is not in EAP
			if (isRestrictedAppEnvironment(environmentType, appId)) {
				/* eslint-disable no-console */
				console.error(
					'Forge rovo open usage is restricted to Forge apps in a non-production environment. Please see https://developer.atlassian.com/platform/forge/cli-reference/environments/ for reference on Forge app environments.',
				);
				/* eslint-disable no-console */
				return;
			}

			const product = getRovoProductFromExtensionType(extensionType);

			if (!cloudId || !product) {
				return false;
			}

			// If agent name is not provided use default rovo chat agent
			const agentId = agentName
				? await getRovoAgentId({
						client: apolloClient,
						agentAppId: appId,
						cloudId,
						input: { name: agentName },
						product,
						agentKey,
					})
				: DEFAULT_AGENT_REFERENCE;

			publish({
				type: ROVO_EVENT_TYPE,
				product,
				data: {
					dialogues: [],
					agentId,
					...(prompt ? { prompt } : {}),
				},
				source: FORGE_SOURCE,
			});
		},
	};
}
